import type { ActivityOf } from '@wyn/content';
import { balance } from '../config/balance.ts';
import { assertGame } from '../errors.ts';
import { createRng, type Rng } from '../rng.ts';
import { DT, TICK_MS } from './realtime.ts';
import { clamp, type MinigameResult, type TurnEngineImpl } from './types.ts';

export type JoustAim = 'helm' | 'chest' | 'shield';
export type JoustGuard = 'high' | 'low';

export type JoustingMove = { type: 'charge'; aim: JoustAim; guard: JoustGuard; /** Tick of the charge at which the lance is couched. */ tick: number };

export const JOUST_PASSES = 3;
/** Where on the charge (0–1) the lances meet. */
export const JOUST_ZONE = 0.72;
export const JOUST_AIMS: Record<JoustAim, { label: string; points: number; window: number }> = {
  helm: { label: 'Helm', points: 3, window: 0.07 },
  chest: { label: 'Chest', points: 2, window: 0.12 },
  shield: { label: 'Shield', points: 1, window: 0.2 },
};
const AIM_TELL: Record<JoustAim, string> = { helm: 'His lance tip rides high.', chest: 'He levels his lance at your breastplate.', shield: 'He angles for your shield.' };
const GUARD_TELL: Record<JoustGuard, string> = { high: 'He hunches behind a raised shield.', low: 'His shield hangs low, covering his belly.' };
/** After a blow to his helm, the foe guards high this often (otherwise half the time). */
const WARY_HIGH = 0.75;

interface Pass {
  foeAim: JoustAim;
  foeGuard: JoustGuard;
  aimTell: JoustAim;
  guardTell: JoustGuard;
}

export interface JoustingState {
  level: number;
  pass: number;
  me: number;
  foe: number;
  unhorsed: 'me' | 'foe' | null;
  current: Pass;
  last: { text: string; mine: number; his: number; timing: number } | null;
  over: boolean;
  /** Your lance found his helm last pass, so he guards high more often. Older sessions have no field. */
  wary?: boolean;
}

export interface JoustingView {
  pass: number;
  passes: number;
  me: number;
  foe: number;
  foeName: string;
  /** What you read in his seat and lance this pass. Tells can lie. */
  tells: string;
  aims: Record<JoustAim, { label: string; points: number; window: number }>;
  zone: number;
  /** Seconds from the start of the charge to the end of the list. */
  duration: number;
  /** The chance a perfect strike to an open helm unhorses him. */
  unhorse: number;
  last: { text: string; mine: number; his: number; timing: number } | null;
  unhorsed: 'me' | 'foe' | null;
  over: boolean;
}

/** How often the foe's tells are true: better with practice beyond the tilt's own level, worse against a better foe. */
const accuracy = (a: ActivityOf<'jousting'>, level: number) => clamp(0.75 + Math.max(0, level - a.level) * 0.02 - a.opponent * 0.25, 0.3, 0.92);
const duration = (a: ActivityOf<'jousting'>) => 1.7 - a.opponent * 0.4;
const window = (aim: JoustAim, level: number) => JOUST_AIMS[aim].window + level * 0.002;
/** The chance a perfect strike to an open helm unhorses him: a steadier seat on a better foe, and practice beyond the tilt. */
export const unhorseChance = (a: ActivityOf<'jousting'>, level: number) => clamp(0.6 - a.opponent * 0.3 + Math.max(0, level - a.level) * 0.01, 0.25, 0.8);

function drawPass(a: ActivityOf<'jousting'>, level: number, rng: Rng, wary = false): Pass {
  const foeAim: JoustAim = rng.next() < 0.3 ? 'helm' : rng.next() < 0.65 ? 'chest' : 'shield';
  const foeGuard: JoustGuard = wary ? (rng.next() < WARY_HIGH ? 'high' : 'low') : rng.pick<JoustGuard>(['high', 'low']);
  const acc = accuracy(a, level);
  const aimTell = rng.next() < acc ? foeAim : rng.pick((Object.keys(JOUST_AIMS) as JoustAim[]).filter((k) => k !== foeAim));
  const guardTell: JoustGuard = rng.next() < acc ? foeGuard : foeGuard === 'high' ? 'low' : 'high';
  return { foeAim, foeGuard, aimTell, guardTell };
}

const blow = (aim: JoustAim, guard: JoustGuard) =>
  aim === 'shield' ? 1 : (aim === 'helm' && guard === 'high') || (aim === 'chest' && guard === 'low') ? 0 : JOUST_AIMS[aim].points;

/** How well a lance couched at `tick` lands: 1 perfect, 0.5 good, 0 a miss. Deterministic, so the client can show it. */
export function joustTiming(a: ActivityOf<'jousting'>, level: number, aim: JoustAim, tick: number): number {
  const pos = (tick * DT) / duration(a);
  if (pos >= 1) return 0;
  const d = Math.abs(pos - JOUST_ZONE), w = window(aim, level);
  return d <= w / 2 ? 1 : d <= w ? 0.5 : 0;
}

/**
 * The Lists: three passes. Read the foe's tells, pick where to strike and what to guard, then couch
 * the lance as the horses meet. A perfect blow to an open helm may unhorse him, and once his helm
 * has been struck he guards it more. The foe's real aim and guard stay on the server. Ported from
 * the Harrowmere prototype.
 */
export const jousting: TurnEngineImpl<ActivityOf<'jousting'>, JoustingState, JoustingMove, JoustingView> = {
  init(a, level, serverSeed) {
    return { level, pass: 1, me: 0, foe: 0, unhorsed: null, current: drawPass(a, level, createRng(serverSeed)), last: null, over: false };
  },

  move(a, s, m, { rng, elapsedMs, level }) {
    assertGame(!s.over, 'INVALID', 'The tournament is over.');
    assertGame(m.aim in JOUST_AIMS && (m.guard === 'high' || m.guard === 'low'), 'INVALID', 'Choose a target and a guard.');
    assertGame(Number.isInteger(m.tick) && m.tick >= 0, 'INVALID', 'Bad timing.');
    assertGame(m.tick * TICK_MS <= elapsedMs + balance.minigames.moveGraceMs, 'INVALID', 'That was quicker than the charge itself.');
    const { foeAim, foeGuard } = s.current;
    const timing = joustTiming(a, level, m.aim, m.tick);
    const mine = timing >= 0.5 ? blow(m.aim, foeGuard) : 0;
    const foeHit = rng.next() < a.opponent;
    const his = foeHit ? blow(foeAim, m.guard) : 0;
    let unhorsed: JoustingState['unhorsed'] = null;
    const helmBlow = mine > 0 && m.aim === 'helm';
    const reels = timing === 1 && helmBlow;
    if (reels && rng.next() < unhorseChance(a, level)) unhorsed = 'foe';
    else if (foeHit && foeAim === 'helm' && m.guard !== 'high' && rng.next() < a.opponent * 0.35) unhorsed = 'me';
    const strike = timing === 0 ? 'You miss' : reels ? `He reels from a blow to the helm (${mine})` : mine ? `You strike for ${mine}` : 'Your lance glances off his guard';
    const text =
      unhorsed === 'foe' ? `${a.foe} is unhorsed!`
      : unhorsed === 'me' ? 'You are unhorsed!'
      : `${strike}; ${his ? `he strikes you for ${his}` : foeHit ? 'you turn his lance aside' : 'he misses'}.`;
    const over = unhorsed !== null || s.pass >= JOUST_PASSES;
    return {
      level: s.level,
      pass: over ? s.pass : s.pass + 1,
      me: s.me + mine,
      foe: s.foe + his,
      unhorsed,
      current: over ? s.current : drawPass(a, level, rng, helmBlow),
      last: { text, mine, his, timing },
      over,
      wary: helmBlow,
    };
  },

  view(a, s) {
    const aims = Object.fromEntries(
      (Object.keys(JOUST_AIMS) as JoustAim[]).map((k) => [k, { ...JOUST_AIMS[k], window: window(k, s.level) }]),
    ) as JoustingView['aims'];
    return {
      pass: s.pass,
      passes: JOUST_PASSES,
      me: s.me,
      foe: s.foe,
      foeName: a.foe,
      tells: `${GUARD_TELL[s.current.guardTell]} ${AIM_TELL[s.current.aimTell]}`,
      aims,
      zone: JOUST_ZONE,
      duration: duration(a),
      unhorse: unhorseChance(a, s.level),
      last: s.last,
      unhorsed: s.unhorsed,
      over: s.over,
    };
  },

  result(a, s): MinigameResult | null {
    if (!s.over) return null;
    const win = s.unhorsed === 'foe' || (!s.unhorsed && s.me > s.foe);
    const draw = !s.unhorsed && s.me === s.foe;
    const coin = win ? a.prize : draw ? Math.round(a.prize / 3) : 0;
    return {
      score: win ? clamp(0.7 + (s.me - s.foe) * 0.05, 0.7, 1) : draw ? 0.45 : 0.2,
      title: win ? 'Victory in the lists!' : draw ? 'A draw' : 'Defeat',
      text: `${win ? 'The crowd roars your name' : draw ? 'The marshal calls it even' : `${a.foe} takes the day`} (${s.me}–${s.foe}).${coin ? ` You receive ${coin} coin.` : ''}`,
      breakdown: [['Points', Math.min(1, s.me / 9)]],
      yield: null,
      coin,
      xpMult: 1,
      feats: [...(s.unhorsed === 'foe' ? ['unhorsed'] : []), ...(s.unhorsed === 'me' ? ['thrown'] : []), ...(win && s.foe === 0 ? ['untouched'] : [])],
    };
  },
};
